import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "../utils/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function DesktopNav({
  scrolled,
  products,
  onCategorySelect,
  onProductSelect,
}) {
  const navigate = useNavigate();
  const { t, resolve } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [expandedGroup, setExpandedGroup] = React.useState(null);
  const buttonRef = React.useRef(null);
  const closeTimerRef = React.useRef(null);
  const open = Boolean(anchorEl);

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const startCloseTimer = React.useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
      setExpandedGroup(null);
    }, 100);
  }, [clearCloseTimer]);

  const handleClose = React.useCallback(() => {
    clearCloseTimer();
    setAnchorEl(null);
    setExpandedGroup(null);
  }, [clearCloseTimer]);

  const btnColor = scrolled ? "#0B0F13" : "#fff";

  return (
    <Stack
      direction="row"
      spacing={5}
      alignItems="center"
      sx={{ display: { xs: "none", md: "flex" } }}
    >
      <Button
        component={RouterLink}
        to="/"
        sx={{ color: btnColor, textTransform: "none" }}
      >
        {t("nav.home")}
      </Button>

      <Box
        onMouseEnter={() => {
          clearCloseTimer();
          setAnchorEl(buttonRef.current);
        }}
        onMouseLeave={startCloseTimer}
        sx={{ position: "relative", display: "inline-block" }}
      >
        <Button
          ref={buttonRef}
          sx={{ color: btnColor, textTransform: "none", cursor: "pointer" }}
          onClick={() =>
            open ? handleClose() : setAnchorEl(buttonRef.current)
          }
          endIcon={<ExpandMoreIcon sx={{ color: btnColor }} />}
        >
          {t("nav.products")}
        </Button>

        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 1300,
              minWidth: 340,
              bgcolor: "#fff",
              color: "#1D2129",
              boxShadow: "0 16px 40px rgba(15,23,42,0.12)",
              overflow: "hidden",
            }}
          >
            {products.map((group, groupIndex) => (
              <Box
                key={group.id}
                sx={{
                  borderBottom:
                    groupIndex < products.length - 1
                      ? "1px solid rgba(0,0,0,0.08)"
                      : "none",
                }}
              >
                <Box
                  onClick={() => {
                    if (group.id === "autec") {
                      onCategorySelect("/autecproducts");
                      handleClose();
                      return;
                    }
                    setExpandedGroup((cur) =>
                      cur === group.id ? null : group.id,
                    );
                  }}
                  sx={{
                    boxSizing: "border-box",
                    pl: 1.25,
                    pr: 2.5,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    cursor: "pointer",
                    bgcolor: "transparent",
                  }}
                >
                  <Box
                    component="img"
                    src={group.image}
                    alt={group.title}
                    sx={{
                      width: 56,
                      height: 56,
                      objectFit: "contain",
                      mr: 1.25,
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: "0.95rem" }}>
                      {group.title}
                    </Typography>
                    {group.id === "autec" && (
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.5)" }}
                      >
                        Exclusive Distributor in Sweden Since 2009
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {group.id !== "autec" &&
                    (expandedGroup === group.id ? (
                      <ExpandLess
                        sx={{ opacity: 0.8, mr: 0.25, flexShrink: 0 }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{ opacity: 0.8, mr: 0.25, flexShrink: 0 }}
                      />
                    ))}
                </Box>

                <Collapse
                  in={group.id !== "autec" && expandedGroup === group.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                    {group.items.map((product, index) => (
                      <Box
                        key={`${group.id}-${product.slug}-${index}`}
                        onClick={() => {
                          onProductSelect(product.slug);
                          handleClose();
                        }}
                        sx={{
                          m: 0,
                          py: 1,
                          pl: 2.5,
                          pr: 2,
                          cursor: "pointer",
                          color: "#1D2129",
                          borderBottom:
                            index < group.items.length - 1
                              ? "1px solid rgba(0,0,0,0.08)"
                              : "none",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                        }}
                      >
                        <Typography sx={{ fontSize: "0.92rem" }}>
                          {resolve(product.title) || resolve(product.name)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}

            <Divider sx={{ my: 0 }} />
            <Box
              onClick={() => {
                onCategorySelect("/products");
                handleClose();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pr: 2.5,
                py: 1.25,
                gap: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <Typography>{t("nav.showAllProducts")}</Typography>
              <ChevronRightIcon sx={{ fontSize: 24, mr: 0.25 }} />
            </Box>
          </Box>
        )}
      </Box>

      <Button
        sx={{ color: btnColor, textTransform: "none" }}
        onClick={() => navigate("/news")}
      >
        {t("nav.news")}
      </Button>
      <Button
        sx={{ color: btnColor, textTransform: "none" }}
        onClick={() => navigate("/about")}
      >
        {t("nav.about")}
      </Button>

      {/* Language switcher */}
      <LanguageSwitcher textColor={btnColor} />
    </Stack>
  );
}
